/**
 * Editor de Planos estilo Canva (full-screen).
 * Carga la imagen base del recuadro de Ficha FFE y permite poner iconos,
 * trazos, recuadros, notas/textos. Al guardar genera un PNG y lo sube a MinIO.
 *
 * Ruta: /editor-planos?cotizacion_id=X&recuadro_uuid=Y&url_index=Z&...
 * Se abre en una nueva pestaña (window.open).
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Stage, Layer, Image as KImage, Line, Rect, Text, Circle, Arrow, Group, Transformer } from 'react-konva';
import useImage from './useImageHook';
import { TIPOS_PLANO, PALETAS, getTipoPlano } from './tiposPlano';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import './EditorPlanos.css';

// Utiles
const uuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getParams = () => {
  const sp = new URLSearchParams(window.location.search);
  return {
    cotizacion_id: parseInt(sp.get('cotizacion_id') || '0'),
    recuadro_uuid: sp.get('recuadro_uuid') || '',
    url_index: parseInt(sp.get('url_index') || '0'),
    titulo: sp.get('titulo') || '',
    proyecto_nombre: sp.get('proyecto_nombre') || '',
    apartamento: sp.get('apartamento') || '',
    imagen_base: sp.get('imagen_base') || '',
  };
};

// Construye URL de imagen del catalogo si la imagen viene como key MinIO
const resolveImageUrl = (raw) => {
  if (!raw) return '';
  if (raw.startsWith('data:')) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  // Asumimos que es un key del bucket catalogo-ffe (formato bucket/key)
  return `${UPLOAD_URL}/catalogo/img/${raw}`;
};

const EditorPlanos = () => {
  const params = useMemo(() => getParams(), []);
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Estado del editor
  const [tipoPlano, setTipoPlano] = useState('existente');
  const [aplica, setAplica] = useState(true);
  const [nodes, setNodes] = useState([]);            // {id, kind, x, y, ...}
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [planoId, setPlanoId] = useState(null);      // ID en BD, si ya existe
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 1000, height: 700 });
  // Al exportar el PNG se ocultan las tarjetas de nota (ref_note): esas ya salen
  // en la leyenda "Referencias" del PDF, así que no deben quemarse en la imagen.
  const [exportando, setExportando] = useState(false);
  // Hoja actual (para tipos con varias: detalle y sus variantes, imagenes_3d). Otros tipos = 0.
  const [hoja, setHoja] = useState(0);
  const [planosTodos, setPlanosTodos] = useState([]); // todos los planos de la cotización (para navegar hojas)

  // Autoguardado
  const [initialized, setInitialized] = useState(false);
  const [autoStatus, setAutoStatus] = useState('idle'); // idle | pending | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const guardandoRef = useRef(false);
  // Refs siempre con el ultimo state (el timeout del debounce capturaria cierres viejos sin esto)
  const nodesRef = useRef(nodes);
  const tipoRef = useRef(tipoPlano);
  const aplicaRef = useRef(aplica);
  const hojaRef = useRef(hoja);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { tipoRef.current = tipoPlano; }, [tipoPlano]);
  useEffect(() => { aplicaRef.current = aplica; }, [aplica]);
  useEffect(() => { hojaRef.current = hoja; }, [hoja]);

  // ¿El tipo actual admite varias hojas? (Detalle y sus variantes, Imágenes 3D)
  const admiteVariasHojas = !!getTipoPlano(tipoPlano)?.multihoja;
  // En detalle e imagenes 3D el plano base se muestra pequeno arriba (la "lupa").
  // Se puede quitar por hoja; queda guardado en el editor_json.
  const [verMiniMapa, setVerMiniMapa] = useState(true);
  const verMiniMapaRef = useRef(true);
  // Nombre propio de esta hoja (se sincroniza con la pestaña Planos Generados)
  const [tituloHoja, setTituloHoja] = useState('');
  const tituloHojaRef = useRef('');
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  useEffect(() => { tituloHojaRef.current = tituloHoja; }, [tituloHoja]);
  useEffect(() => { verMiniMapaRef.current = verMiniMapa; }, [verMiniMapa]);
  // Hojas existentes para el tipo actual (siempre incluye 0)
  const hojasDelTipo = [...new Set([0, ...planosTodos.filter(p => p.tipo_plano === tipoPlano).map(p => p.hoja || 0)])].sort((a, b) => a - b);

  // Imagen base
  const baseUrl = resolveImageUrl(params.imagen_base);
  const [baseImage] = useImage(baseUrl, 'anonymous');

  // Migracion: muros viejos guardados como kind:'line' se convierten a 'wall'
  // (Rect delgado). Mantiene compatibilidad con planos previos.
  const migrateNodes = (rawNodes) => {
    // Migrar tarjetas viejas 'ref_note' → descripcion del 'ref' del mismo num,
    // y descartarlas (ahora la descripción se edita en el panel Referencias).
    const src = rawNodes || [];
    const notaTextoPorNum = {};
    for (const n of src) { if (n && n.kind === 'ref_note' && n.num != null && n.text) notaTextoPorNum[n.num] = n.text; }
    const sinNotas = src.filter(n => n && n.kind !== 'ref_note').map(n => {
      if (n.kind === 'ref' && n.descripcion == null && notaTextoPorNum[n.num] != null) {
        return { ...n, descripcion: notaTextoPorNum[n.num] };
      }
      return n;
    });
    return sinNotas.map(n => {
    if (n.kind !== 'line') return n;
    const pts = n.points || [0, 0, 0, 0];
    const [x1, y1, x2, y2] = pts;
    const dx = x2 - x1, dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy) || 100;
    const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
    const grosor = n.strokeWidth || 6;
    const midX = (n.x || 0) + (x1 + x2) / 2;
    const midY = (n.y || 0) + (y1 + y2) / 2;
    return {
      id: n.id, kind: 'wall',
      x: midX - length / 2, y: midY - grosor / 2,
      width: length, height: grosor,
      rotation: (n.rotation || 0) + angleDeg,
      fill: n.stroke || '#111827', label: n.label || 'Muro',
    };
    });
  };

  // Cargar plano para un tipo+hoja especifico
  const cargarPlanoDeTipo = useCallback(async (tipo, hojaN = 0) => {
    if (!params.cotizacion_id || !tipo) return null;
    try {
      const data = await client.query(
        `query($cid: Int!, $tipo: String!, $hoja: Int) {
          planoGenerado(cotizacion_id: $cid, tipo_plano: $tipo, hoja: $hoja) {
            id tipo_plano hoja aplica editor_json titulo
          }
        }`,
        { cid: params.cotizacion_id, tipo, hoja: hojaN }
      );
      if (data.planoGenerado) {
        const ej = data.planoGenerado.editor_json;
        return {
          id: data.planoGenerado.id,
          aplica: data.planoGenerado.aplica !== false,
          nodes: ej && Array.isArray(ej.nodes) ? migrateNodes(ej.nodes) : [],
          verMiniMapa: ej?.verMiniMapa !== false,
          titulo: data.planoGenerado.titulo || '',
        };
      }
      return null;
    } catch (e) {
      console.error('Error cargando plano tipo', tipo, e);
      return null;
    }
  }, [params.cotizacion_id]);

  // Cambiar a otra hoja del tipo actual (guarda la actual, carga la nueva).
  // hojaNueva === null → crea una hoja nueva (canvas vacío).
  const irAHoja = async (hojaNueva) => {
    // 1) Guardar la hoja actual (síncrono) antes de cambiar
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    if (initialized && nodesRef.current.length > 0) {
      await handleGuardarRef.current({ silent: true });
    }
    const destino = hojaNueva === null ? (Math.max(-1, ...hojasDelTipo) + 1) : hojaNueva;
    if (destino === hojaRef.current) return;
    // 2) Refs + limpiar canvas
    hojaRef.current = destino;
    nodesRef.current = [];
    aplicaRef.current = true;
    setHistory([]); setRedoStack([]); setSelectedId(null);
    setNodes([]); setPlanoId(null); setAplica(true);
    setHoja(destino);
    // 3) Cargar la hoja (si existe)
    const plano = await cargarPlanoDeTipo(tipoRef.current, destino);
    if (plano) {
      setPlanoId(plano.id); setAplica(plano.aplica); setNodes(plano.nodes);
      nodesRef.current = plano.nodes; aplicaRef.current = plano.aplica;
      setVerMiniMapa(plano.verMiniMapa !== false); verMiniMapaRef.current = plano.verMiniMapa !== false;
      setTituloHoja(plano.titulo || ''); tituloHojaRef.current = plano.titulo || '';
    }
  };

  // Carga inicial: trae TODOS los planos del recuadro y muestra el último
  // editado. Si no hay ninguno, queda vacío en 'existente'. Esto evita el bug
  // donde al abrir el editor se quedaba siempre en 'existente' y al guardar
  // sobrescribía el plano de 'existente' (ignorando los otros tipos guardados).
  useEffect(() => {
    if (!params.cotizacion_id || !params.recuadro_uuid) {
      setInitialized(true);
      return;
    }
    (async () => {
      try {
        const data = await client.query(
          `query($cid: Int!) {
            planosGenerados(cotizacion_id: $cid) {
              id recuadro_uuid url_index tipo_plano hoja aplica editor_json titulo updated_at
            }
          }`,
          { cid: params.cotizacion_id }
        );
        // Todos los planos del proyecto. Mostramos el ultimo editado al abrir;
        // el dropdown cambia de tipo y (para Detalle/3D) hay selector de hoja.
        const planosCot = data.planosGenerados || [];
        setPlanosTodos(planosCot);
        if (planosCot.length > 0) {
          const orden = [...planosCot].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
          const ultimo = orden[0];
          setTipoPlano(ultimo.tipo_plano || 'existente');
          setHoja(ultimo.hoja || 0);
          hojaRef.current = ultimo.hoja || 0;
          setPlanoId(ultimo.id);
          setAplica(ultimo.aplica !== false);
          const ej = ultimo.editor_json;
          if (ej && Array.isArray(ej.nodes)) setNodes(migrateNodes(ej.nodes));
        }
      } catch (e) {
        console.error('Error en carga inicial', e);
      } finally {
        setTimeout(() => setInitialized(true), 100);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Ajustar tamaño del canvas al area disponible
  useEffect(() => {
    const update = () => {
      const wrap = document.querySelector('.epl-canvas-area');
      if (!wrap) return;
      const r = wrap.getBoundingClientRect();
      setStageSize({ width: Math.max(300, r.width - 24), height: Math.max(300, r.height - 24) });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Calcular escala de la imagen base. En "Plano de Detalle" la imagen sale
  // pequeña arriba a la izquierda (mini-mapa) y dejamos el resto del canvas
  // libre para que el usuario ponga imagenes/flechas/notas. En el resto de
  // tipos, la imagen ocupa el canvas completo (fit centrada).
  const baseRender = useMemo(() => {
    if (!baseImage) return null;
    const stageW = stageSize.width, stageH = stageSize.height;
    const imgRatio = baseImage.width / baseImage.height;

    // Tipos que usan mini-mapa: detalle (y sus variantes) e imagenes_3d. El plano
    // base se ve pequeño arriba-DERECHA y el resto del canvas queda libre para que
    // el usuario ponga imagenes/renders/flechas/notas a la izquierda.
    if (getTipoPlano(tipoPlano)?.miniMapa) {
      if (!verMiniMapa) return null;   // el usuario apago la lupa en esta hoja
      const maxW = stageW * 0.28;
      const maxH = stageH * 0.35;
      let w, h;
      if (maxW / maxH > imgRatio) { h = maxH; w = h * imgRatio; }
      else { w = maxW; h = w / imgRatio; }
      return { x: stageW - w - 8, y: 8, width: w, height: h, mini: true };
    }

    const stageRatio = stageW / stageH;
    let w, h;
    if (imgRatio > stageRatio) { w = stageW; h = stageW / imgRatio; }
    else { h = stageH; w = stageH * imgRatio; }
    return { x: (stageW - w) / 2, y: (stageH - h) / 2, width: w, height: h, mini: false };
  }, [baseImage, stageSize, tipoPlano, verMiniMapa]);

  // Snapshot historial
  const pushHistory = useCallback((nextNodes) => {
    setHistory(h => [...h.slice(-49), nodes]);
    setRedoStack([]);
    setNodes(nextNodes);
  }, [nodes]);

  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setRedoStack(r => [...r, nodes]);
    setHistory(h => h.slice(0, -1));
    setNodes(prev);
    setSelectedId(null);
  };
  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setHistory(h => [...h, nodes]);
    setRedoStack(r => r.slice(0, -1));
    setNodes(next);
    setSelectedId(null);
  };

  // Crear nodo desde paleta
  const addFromPalette = (item) => {
    const cx = stageSize.width / 2;
    const cy = stageSize.height / 2;
    let node;
    if (item.src) {
      node = { id: uuid(), kind: 'icon', src: item.src, label: item.label, x: cx - 20, y: cy - 20, width: item.w || 40, height: item.w || 40, rotation: 0 };
    } else if (item.shape === 'line') {
      // Los muros se modelan como rectangulos delgados (no como Line). El
      // Transformer de Konva maneja resize/rotate sobre Rect de forma nativa
      // y consistente, mientras que con Line los puntos relativos se
      // desincronizan tras el primer transform.
      const grosor = item.strokeWidth || 6;
      const largo = 140;
      node = {
        id: uuid(),
        kind: 'wall',
        x: cx - largo / 2,
        y: cy - grosor / 2,
        width: largo,
        height: grosor,
        rotation: 0,
        fill: item.stroke,
        label: item.label,
      };
    } else if (item.shape === 'rect_border' || item.shape === 'rect_fill') {
      node = { id: uuid(), kind: 'rect', x: cx - 60, y: cy - 40, width: 120, height: 80, rotation: 0, stroke: item.stroke, strokeWidth: item.strokeWidth, fill: item.fill || 'transparent', label: item.label };
    } else {
      return;
    }
    pushHistory([...nodes, node]);
    setSelectedId(node.id);
  };

  const addText = () => {
    const cx = stageSize.width / 2, cy = stageSize.height / 2;
    const node = { id: uuid(), kind: 'text', x: cx - 50, y: cy - 10, text: 'Doble click para editar', fontSize: 18, fill: '#111827', rotation: 0, width: 200 };
    pushHistory([...nodes, node]);
    setSelectedId(node.id);
  };

  const addNote = () => {
    const cx = stageSize.width / 2, cy = stageSize.height / 2;
    const node = { id: uuid(), kind: 'note', x: cx - 80, y: cy - 30, width: 160, height: 60, text: 'Nota...', fontSize: 14, fill: '#111827', bg: 'rgba(254, 240, 138, 0.9)' };
    pushHistory([...nodes, node]);
    setSelectedId(node.id);
  };

  // Flecha (simple, doble o curva). Group con linea delgada + puntas pequeñas.
  // El Group hereda scale del Transformer, asi resize/rotate funcionan nativos.
  const addArrow = (variant = 'simple') => {
    const cx = stageSize.width / 2, cy = stageSize.height / 2;
    const w = variant === 'curve' ? 180 : 160;
    const h = variant === 'curve' ? 60 : 16;
    const node = {
      id: uuid(),
      kind: 'arrow',
      variant, // 'simple' | 'double' | 'curve'
      x: cx - w / 2, y: cy - h / 2,
      width: w, height: h,
      rotation: 0,
      stroke: '#1c2837',
      strokeWidth: 2,
      label: variant === 'double' ? 'Flecha doble' : variant === 'curve' ? 'Flecha curva' : 'Flecha',
    };
    pushHistory([...nodes, node]);
    setSelectedId(node.id);
  };

  // Referencia numerada: crea SOLO el círculo numerado en el plano. La
  // descripción se escribe en el panel "Referencias" del sidebar (no en una
  // tarjeta sobre el plano) y sale únicamente en la columna del PDF.
  const addRefNum = () => {
    const cx = stageSize.width / 2, cy = stageSize.height / 2;
    const usados = nodes.filter(n => n.kind === 'ref').map(n => n.num);
    let next = 1;
    while (usados.includes(next)) next++;
    const circulo = {
      id: uuid(),
      kind: 'ref',
      x: cx - 16, y: cy - 16,
      width: 32, height: 32,
      num: next,
      rotation: 0,
      descripcion: '',
      label: `Referencia ${next}`,
    };
    pushHistory([...nodes, circulo]);
    setSelectedId(circulo.id);
  };

  const updateNode = (id, patch) => {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, ...patch } : n));
  };
  const commitChange = () => { setHistory(h => [...h.slice(-49), nodes]); setRedoStack([]); };

  const deleteSelected = () => {
    if (!selectedId) return;
    pushHistory(nodes.filter(n => n.id !== selectedId));
    setSelectedId(null);
  };

  // Atajos de teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') { deleteSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [selectedId, nodes, history, redoStack]);

  // Attach transformer al nodo seleccionado
  useEffect(() => {
    const tr = transformerRef.current;
    const layer = layerRef.current;
    if (!tr || !layer) return;
    if (!selectedId) { tr.nodes([]); tr.getLayer()?.batchDraw(); return; }
    const target = layer.findOne(`#${selectedId}`);
    if (target) {
      tr.nodes([target]);
      tr.getLayer()?.batchDraw();
    }
  }, [selectedId, nodes]);

  // Click en stage vacio deselecciona
  const handleStageClick = (e) => {
    if (e.target === e.target.getStage()) setSelectedId(null);
  };

  // Doble click en text/note para editar inline
  const handleDblClick = (node) => {
    const stage = stageRef.current;
    if (!stage) return;
    const abs = stage.findOne(`#${node.id}`).getAbsolutePosition();
    const stageBox = stage.container().getBoundingClientRect();
    const area = document.createElement('textarea');
    area.value = node.text;
    area.style.position = 'absolute';
    area.style.top = `${stageBox.top + abs.y + window.scrollY}px`;
    area.style.left = `${stageBox.left + abs.x + window.scrollX}px`;
    area.style.width = `${(node.width || 200)}px`;
    area.style.height = `${(node.kind === 'note' ? node.height : node.fontSize + 10)}px`;
    area.style.fontSize = `${node.fontSize || 16}px`;
    area.style.border = '2px solid #3b82f6';
    area.style.background = node.kind === 'note' ? '#fef9c3' : 'white';
    area.style.padding = '4px';
    area.style.zIndex = 9999;
    document.body.appendChild(area);
    area.focus();
    const finish = () => {
      const val = area.value;
      updateNode(node.id, { text: val });
      commitChange();
      area.remove();
    };
    area.addEventListener('blur', finish);
    area.addEventListener('keydown', (e) => { if (e.key === 'Escape') area.blur(); });
  };

  // Subir imagen externa (3D / fotos)
  const handleSubirImagen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      const cx = stageSize.width / 2, cy = stageSize.height / 2;
      const node = { id: uuid(), kind: 'icon', src, label: file.name, x: cx - 100, y: cy - 75, width: 200, height: 150, rotation: 0 };
      pushHistory([...nodes, node]);
      setSelectedId(node.id);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Guardar plano (opciones: silent → no muestra toast, usado por autoguardado)
  const handleGuardar = async ({ silent = false } = {}) => {
    const stage = stageRef.current;
    if (!stage) return;
    if (guardandoRef.current) return; // evitar guardados concurrentes
    guardandoRef.current = true;
    if (!silent) setGuardando(true);
    setAutoStatus('saving');
    if (!silent) setMensaje(null);

    // Deseleccionar para que el transformer no salga en el PNG, y ocultar las
    // tarjetas de nota (ref_note) para que no se dupliquen con la leyenda del PDF.
    const prevSelected = selectedId;
    setSelectedId(null);
    setExportando(true);
    await new Promise(r => setTimeout(r, 60));
    try {
      // El lienzo es del tamano de la ventana, asi que casi siempre sobra
      // espacio en blanco alrededor del plano. Si se exporta completo, al
      // encajarlo en el PDF el plano queda pequenito. Por eso se recorta al
      // area realmente dibujada y se sube la resolucion para que no pixele.
      let opciones = { pixelRatio: 2, mimeType: 'image/png' };
      // En detalle / imagenes 3D NO se recorta: ahi la posicion importa (la lupa
      // va arriba a la derecha y los renders alrededor), y recortar al contenido
      // rompia esa composicion. En los demas tipos si se recorta para que el
      // plano llene la pagina del PDF.
      const conservarComposicion = !!getTipoPlano(tipoRef.current)?.miniMapa;
      try {
        const capa = conservarComposicion ? null : layerRef.current;
        const bb = capa && capa.getClientRect({ skipShadow: true });
        if (bb && bb.width > 40 && bb.height > 40) {
          const margen = 14;
          const x = Math.max(0, Math.floor(bb.x - margen));
          const y = Math.max(0, Math.floor(bb.y - margen));
          const w = Math.min(stage.width() - x, Math.ceil(bb.width + margen * 2));
          const h = Math.min(stage.height() - y, Math.ceil(bb.height + margen * 2));
          if (w > 40 && h > 40) {
            opciones = { ...opciones, x, y, width: w, height: h, pixelRatio: Math.min(4, Math.max(2, 2400 / w)) };
          }
        }
      } catch { /* si algo falla, se exporta el lienzo completo como antes */ }
      const dataURL = stage.toDataURL(opciones);
      const token = localStorage.getItem('tuvi_token');
      const resp = await fetch(`${UPLOAD_URL}/plano-generado`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cotizacion_id: params.cotizacion_id,
          recuadro_uuid: params.recuadro_uuid,
          url_index: params.url_index,
          tipo_plano: tipoRef.current,
          hoja: hojaRef.current,
          aplica: aplicaRef.current,
          titulo: tituloHojaRef.current || null,
          proyecto_nombre: params.proyecto_nombre,
          apartamento: params.apartamento,
          imagen_base_url: params.imagen_base,
          editor_json: { nodes: nodesRef.current, verMiniMapa: verMiniMapaRef.current, version: 1 },
          imagen_dataurl: dataURL,
        }),
      });
      const json = await resp.json();
      if (!json.success) throw new Error(json.message || 'Error al guardar');
      setPlanoId(json.plano_id);
      // El editor vive en otra ventana: avisar a la Ficha FFE para que refresque
      // sus planos sin tener que salir y volver a entrar.
      try {
        const cid = Number(params.cotizacion_id) || null;
        if ('BroadcastChannel' in window) {
          const ch = new BroadcastChannel('tuvi-planos');
          ch.postMessage({ tipo: 'plano-guardado', cotizacion_id: cid });
          // Cerrarlo de inmediato puede descartar el mensaje en algunos navegadores.
          setTimeout(() => { try { ch.close(); } catch { /* ya cerrado */ } }, 1500);
        }
        // Respaldo para navegadores sin BroadcastChannel (dispara 'storage' en las otras pestañas)
        localStorage.setItem('tuvi_plano_guardado', JSON.stringify({ cotizacion_id: cid, ts: Date.now() }));
      } catch { /* si falla el aviso, el refresco por foco lo cubre */ }
      // Mantener la lista de planos (para el selector de hojas) al día
      const t = tipoRef.current, h = hojaRef.current;
      setPlanosTodos(prev => {
        const otros = prev.filter(p => !(p.tipo_plano === t && (p.hoja || 0) === h));
        return [...otros, { id: json.plano_id, tipo_plano: t, hoja: h, aplica: aplicaRef.current, updated_at: new Date().toISOString() }];
      });
      setLastSavedAt(new Date());
      setAutoStatus('saved');
      if (!silent) setMensaje({ tipo: 'ok', texto: '✓ Plano guardado correctamente' });
    } catch (e) {
      setAutoStatus('error');
      setMensaje({ tipo: 'err', texto: 'Error: ' + e.message });
    } finally {
      guardandoRef.current = false;
      setExportando(false); // volver a mostrar las tarjetas de nota en el editor
      if (!silent) setGuardando(false);
      // Restaurar selección que estaba antes de guardar (autoguardado no debe perder foco)
      if (silent && prevSelected) setSelectedId(prevSelected);
      if (!silent) setTimeout(() => setMensaje(null), 3500);
    }
  };

  // Ref siempre apuntando a la version actual del handler (evita closures viejas)
  const handleGuardarRef = useRef(handleGuardar);
  useEffect(() => { handleGuardarRef.current = handleGuardar; });

  // Autoguardado debounced: tras cada cambio en nodes/tipoPlano/aplica
  // esperar 2 segundos sin nuevos cambios y guardar silencioso.
  useEffect(() => {
    if (!initialized) return;
    if (!params.cotizacion_id || !params.recuadro_uuid) return;
    setAutoStatus('pending');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleGuardarRef.current({ silent: true });
    }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    // eslint-disable-next-line
  }, [nodes, tipoPlano, aplica, initialized]);

  // Guardar tambien al cerrar la pestaña (best-effort: navigator.sendBeacon no
  // funciona bien con dataURL grande, pero al menos intentamos un fetch sync).
  useEffect(() => {
    const beforeUnload = () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        // Disparar guardado inmediato sin esperar.
        handleGuardarRef.current({ silent: true });
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  const handleSoloNA = async () => {
    setAplica(false);
    if (!planoId) {
      // Crear registro minimo (sin imagen) marcando aplica=false
      // No usamos imagen_dataurl, asi que el endpoint requiere uno. Para N/A
      // sin imagen previa, mejor llamamos mutation marcarAplicaPlano si ya existe,
      // o creamos uno trivial con imagen base vacia.
      // Solucion: snapshot del canvas vacio.
      handleGuardar({});
      return;
    }
    try {
      await client.mutate(`mutation($id: Int!, $a: Boolean!) { marcarAplicaPlano(id: $id, aplica: $a) { success } }`, { id: planoId, a: false });
      setMensaje({ tipo: 'ok', texto: 'Marcado como N/A' });
    } catch (e) {
      setMensaje({ tipo: 'err', texto: 'Error: ' + e.message });
    }
  };

  const tipoActual = getTipoPlano(tipoPlano);
  const paleta = PALETAS[tipoPlano] || [];

  // Los íconos de los planos Eléctrico e Iluminación solo pueden rotarse, no
  // redimensionarse. Se detectan por la ruta del icono (carpeta ELECTRICO/ILUMINACION),
  // así también aplica a planos guardados antes de esta restricción.
  const nodoSeleccionado = nodes.find(n => n.id === selectedId);
  const soloRotacion = nodoSeleccionado?.kind === 'icon'
    && /\/(ELECTRICO|ILUMINACION)\//i.test(nodoSeleccionado?.src || '');
  // Los marcadores numéricos de referencia solo se pueden mover (ni redimensionar
  // ni rotar) para que conserven siempre el mismo tamaño.
  const soloMover = nodoSeleccionado?.kind === 'ref';

  return (
    <div className="epl-root">
      {/* Toolbar superior */}
      <div className="epl-toolbar">
        <button className="epl-btn epl-btn-secondary" onClick={() => window.close()}>← Volver</button>
        <div className="epl-toolbar-title">
          🎨 Editor de Planos
          {params.titulo && <span className="epl-toolbar-sub"> · {params.titulo}</span>}
          {params.apartamento && <span className="epl-toolbar-sub"> · Apto {params.apartamento}</span>}
        </div>
        <div className="epl-toolbar-actions">
          <button className="epl-btn epl-btn-ghost" onClick={undo} disabled={history.length === 0} title="Deshacer (Ctrl+Z)">↶</button>
          <button className="epl-btn epl-btn-ghost" onClick={redo} disabled={redoStack.length === 0} title="Rehacer (Ctrl+Y)">↷</button>
          <button className="epl-btn epl-btn-ghost" onClick={deleteSelected} disabled={!selectedId} title="Eliminar (Del)">🗑</button>
          <AutoSaveIndicator status={autoStatus} lastSavedAt={lastSavedAt} />
          <button className="epl-btn epl-btn-primary" onClick={() => handleGuardar()} disabled={guardando}>
            {guardando ? 'Guardando...' : '💾 Guardar plano'}
          </button>
        </div>
      </div>

      {/* Mensaje flotante */}
      {mensaje && (
        <div className={`epl-toast epl-toast-${mensaje.tipo}`}>{mensaje.texto}</div>
      )}

      <div className="epl-body">
        {/* Sidebar izquierdo */}
        <aside className="epl-sidebar epl-sidebar-left">
          <div className="epl-section">
            <div className="epl-section-title">Tipo de plano</div>
            <select
              className="epl-select"
              value={tipoPlano}
              onChange={async (e) => {
                const nuevoTipo = e.target.value;
                if (nuevoTipo === tipoPlano) return;
                // 1) Cancelar autosave pendiente y guardar plano actual sincrono.
                //    tipoRef.current todavia tiene el tipo VIEJO -> se guarda alli.
                if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
                if (initialized && nodesRef.current.length > 0) {
                  await handleGuardarRef.current({ silent: true });
                }
                // 2) Actualizar refs SINCRONICAMENTE antes de cambiar state
                //    (asi cualquier autosave que dispare desde aqui usa el tipo NUEVO).
                tipoRef.current = nuevoTipo;
                hojaRef.current = 0;
                nodesRef.current = [];
                aplicaRef.current = true;
                // 3) Limpiar canvas y cargar plano del nuevo tipo (hoja 0)
                setHistory([]); setRedoStack([]); setSelectedId(null);
                setNodes([]); setPlanoId(null); setAplica(true);
                setTipoPlano(nuevoTipo); setHoja(0);
                const plano = await cargarPlanoDeTipo(nuevoTipo, 0);
                if (plano) {
                  setPlanoId(plano.id);
                  setAplica(plano.aplica);
                  setNodes(plano.nodes);
                  setVerMiniMapa(plano.verMiniMapa !== false);
                  verMiniMapaRef.current = plano.verMiniMapa !== false;
                  setTituloHoja(plano.titulo || '');
                  tituloHojaRef.current = plano.titulo || '';
                  nodesRef.current = plano.nodes;
                  aplicaRef.current = plano.aplica;
                }
              }}
            >
              {TIPOS_PLANO.map(t => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
            {tipoActual && <div className="epl-tipo-desc">{tipoActual.descripcion}</div>}
          </div>

          {/* Selector de hojas: solo para tipos con varias (Detalle, Imágenes 3D) */}
          {admiteVariasHojas && (
            <div className="epl-section">
              <div className="epl-section-title">Hoja</div>
              <div className="epl-hojas-row">
                {hojasDelTipo.map(h => (
                  <button
                    key={h}
                    className={`epl-hoja-btn ${h === hoja ? 'sel' : ''}`}
                    onClick={() => irAHoja(h)}
                    title={`Hoja ${h + 1}`}
                  >{h + 1}</button>
                ))}
                <button className="epl-hoja-btn nueva" onClick={() => irAHoja(null)} title="Agregar una hoja nueva">+ Hoja</button>
              </div>
              <div className="epl-tipo-desc">Puedes agregar cuantas hojas necesites para este tipo.</div>
              <label className="epl-checkbox-row" style={{ marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={verMiniMapa}
                  onChange={e => setVerMiniMapa(e.target.checked)}
                />
                <span>Mostrar plano pequeño (lupa)</span>
              </label>
            </div>
          )}

          <div className="epl-section">
            <label className="epl-checkbox-row">
              <input type="checkbox" checked={aplica} onChange={e => setAplica(e.target.checked)} />
              <span>Aplica al proyecto</span>
            </label>
            {!aplica && (
              <button className="epl-btn epl-btn-warn epl-btn-full" onClick={handleSoloNA}>
                Marcar como N/A
              </button>
            )}
          </div>

          <div className="epl-section">
            <div className="epl-section-title">Elementos en el plano ({nodes.length})</div>
            <div className="epl-nodes-list">
              {nodes.map(n => (
                <div
                  key={n.id}
                  className={`epl-node-row ${selectedId === n.id ? 'sel' : ''}`}
                  onClick={() => setSelectedId(n.id)}
                >
                  <span className="epl-node-icon">
                    {n.kind === 'icon' ? '🖼' : (n.kind === 'wall' || n.kind === 'line') ? '▬' : n.kind === 'rect' ? '▭' : n.kind === 'note' ? '📝' : '📄'}
                  </span>
                  <span className="epl-node-label">{n.label || n.text?.slice(0, 20) || n.kind}</span>
                </div>
              ))}
              {nodes.length === 0 && (
                <div className="epl-empty">Vacío. Agrega elementos desde la paleta →</div>
              )}
            </div>
          </div>
        </aside>

        {/* Canvas central */}
        <main className="epl-canvas-area">
          {/* Vista previa del encabezado del PDF (logo + nombre del plano).
              Va superpuesto y NO entra en el toDataURL, asi que no se duplica
              en el PDF: alli lo dibuja el backend. */}
          <div className="epl-header-preview">
            <img src="/logo.png" alt="" className="epl-header-logo" />
            <div className="epl-header-caja">
              {editandoTitulo ? (
                <input
                  className="epl-header-input"
                  autoFocus
                  defaultValue={tituloHoja}
                  placeholder={tipoActual?.nombre || tipoPlano}
                  onBlur={(e) => { setTituloHoja(e.target.value.trim()); setEditandoTitulo(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur();
                    if (e.key === 'Escape') setEditandoTitulo(false);
                  }}
                />
              ) : (
                <div
                  className="epl-header-titulo epl-header-editable"
                  title="Clic para cambiar el nombre de este plano"
                  onClick={() => setEditandoTitulo(true)}
                >
                  {tituloHoja || tipoActual?.nombre || tipoPlano}
                  {!tituloHoja && admiteVariasHojas && <span className="epl-header-hoja"> — Hoja {(hoja || 0) + 1}</span>}
                  <span className="epl-header-lapiz">✎</span>
                </div>
              )}
              <div className="epl-header-sub">
                {params.proyecto_nombre || 'Proyecto'}{params.apartamento ? ` · Apto ${params.apartamento}` : ''}
              </div>
            </div>
          </div>
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            onMouseDown={handleStageClick}
            onTouchStart={handleStageClick}
            className="epl-stage"
          >
            <Layer ref={layerRef}>
              {/* Imagen base. En plano de detalle / imagenes 3D se renderiza
                   como mini-mapa en la esquina superior DERECHA con borde
                   discreto. En el resto, ocupa el canvas centrada. */}
              {baseImage && baseRender && (
                <>
                  <KImage
                    image={baseImage}
                    x={baseRender.x}
                    y={baseRender.y}
                    width={baseRender.width}
                    height={baseRender.height}
                    listening={false}
                    // Konva habilita imageSmoothing por default a true en context.
                    // Al cache-ar la imagen Konva pre-renderiza una textura
                    // crisp para el tamaño actual del nodo, lo cual mejora la
                    // calidad del mini-mapa (sin esto, el browser hace un
                    // downsample en cada redraw y se ve borroso).
                    ref={(node) => {
                      if (!node) return;
                      try {
                        node.cache({ pixelRatio: 2 });
                        node.getLayer()?.batchDraw();
                      } catch {}
                    }}
                  />
                  {baseRender.mini && (
                    <Rect
                      x={baseRender.x - 2}
                      y={baseRender.y - 2}
                      width={baseRender.width + 4}
                      height={baseRender.height + 4}
                      stroke="#1c2837"
                      strokeWidth={1.5}
                      listening={false}
                    />
                  )}
                </>
              )}
              {/* Nodos */}
              {nodes.map(n => {
                const common = {
                  id: n.id,
                  draggable: true,
                  onClick: () => setSelectedId(n.id),
                  onTap: () => setSelectedId(n.id),
                  onDragEnd: (e) => { updateNode(n.id, { x: e.target.x(), y: e.target.y() }); commitChange(); },
                  onTransformEnd: (e) => {
                    const node = e.target;
                    const scaleX = node.scaleX(), scaleY = node.scaleY();
                    node.scaleX(1); node.scaleY(1);
                    // Muros (Rect delgados) y demas shapes basadas en width/height.
                    // 'wall' permite alto chico (3px) sin clamp del minimo general.
                    const minSize = n.kind === 'wall' ? 1 : 8;
                    updateNode(n.id, {
                      x: node.x(), y: node.y(), rotation: node.rotation(),
                      width: Math.max(minSize, (n.width || node.width()) * scaleX),
                      height: Math.max(minSize, (n.height || node.height()) * scaleY),
                    });
                    commitChange();
                  },
                };
                if (n.kind === 'icon') {
                  return <IconNode key={n.id} node={n} common={common} />;
                }
                if (n.kind === 'wall') {
                  return (
                    <Rect
                      {...common}
                      x={n.x} y={n.y}
                      width={n.width} height={n.height}
                      rotation={n.rotation || 0}
                      fill={conAlpha(n.fill)}
                      cornerRadius={Math.min(2, n.height / 3)}
                    />
                  );
                }
                if (n.kind === 'arrow') {
                  return (
                    <ArrowNode key={n.id} node={n} common={common} />
                  );
                }
                if (n.kind === 'ref') {
                  return (
                    <RefNumNode key={n.id} node={n} common={common} />
                  );
                }
                if (n.kind === 'ref_note') {
                  // No incluir las tarjetas de nota en el PNG exportado (evita
                  // duplicado con la leyenda "Referencias" del PDF)
                  if (exportando) return null;
                  return (
                    <RefNoteNode key={n.id} node={n} common={common} onDblClick={() => handleDblClick(n)} />
                  );
                }
                // Compat: muros viejos guardados como 'line' (raros). Se renderizan
                // pero al editarlos, el handler los migra al modelo wall.
                if (n.kind === 'line') {
                  return (
                    <Line
                      {...common}
                      x={n.x || 0}
                      y={n.y || 0}
                      points={n.points}
                      stroke={n.stroke}
                      strokeWidth={n.strokeWidth}
                      rotation={n.rotation || 0}
                      lineCap="round"
                      lineJoin="round"
                      hitStrokeWidth={24}
                    />
                  );
                }
                if (n.kind === 'rect') {
                  return (
                    <Rect
                      {...common}
                      x={n.x} y={n.y} width={n.width} height={n.height}
                      stroke={n.stroke} strokeWidth={n.strokeWidth}
                      fill={n.fill || 'transparent'} rotation={n.rotation || 0}
                    />
                  );
                }
                if (n.kind === 'text') {
                  return (
                    <Text
                      {...common}
                      x={n.x} y={n.y} text={n.text}
                      fontSize={n.fontSize} fill={n.fill}
                      width={n.width} rotation={n.rotation || 0}
                      onDblClick={() => handleDblClick(n)} onDblTap={() => handleDblClick(n)}
                    />
                  );
                }
                if (n.kind === 'note') {
                  return (
                    <NoteNode
                      key={n.id}
                      node={n}
                      common={common}
                      onDblClick={() => handleDblClick(n)}
                    />
                  );
                }
                return null;
              })}
              <Transformer
                ref={transformerRef}
                rotateEnabled={!soloMover}
                rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                rotationSnapTolerance={4}
                rotateAnchorOffset={28}
                anchorSize={10}
                anchorStroke="#3b82f6"
                anchorFill="white"
                anchorCornerRadius={2}
                borderStroke="#3b82f6"
                borderDash={[4, 4]}
                enabledAnchors={(soloRotacion || soloMover) ? [] : ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right']}
                resizeEnabled={!soloRotacion && !soloMover}
                ignoreStroke
                boundBoxFunc={(oldBox, newBox) => {
                  // Solo bloquear si el bbox queda <2px en cualquier dimension
                  // (los muros pueden tener 3-6px de alto, no se les debe bloquear).
                  if (newBox.width < 2 || newBox.height < 2) return oldBox;
                  return newBox;
                }}
              />
            </Layer>
          </Stage>
        </main>

        {/* Sidebar derecho - paleta */}
        <aside className="epl-sidebar epl-sidebar-right">
          <div className="epl-section">
            <div className="epl-section-title">Herramientas comunes</div>
            <button className="epl-palette-btn" onClick={addText}>🅰 Agregar texto</button>
            <button className="epl-palette-btn" onClick={addNote}>📝 Agregar nota</button>
            <button className="epl-palette-btn" onClick={addRefNum}>① Referencia numerada</button>
            <button className="epl-palette-btn" onClick={() => addArrow('simple')}>→ Flecha 1 punta</button>
            <button className="epl-palette-btn" onClick={() => addArrow('double')}>↔ Flecha 2 puntas</button>
            <button className="epl-palette-btn" onClick={() => addArrow('curve')}>↪ Flecha curva</button>
            <button className="epl-palette-btn" onClick={() => fileInputRef.current?.click()}>📤 Subir imagen / foto</button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleSubirImagen} style={{ display: 'none' }} />
          </div>

          {/* Panel de Referencias: la descripción de cada número se escribe aquí
              (el plano solo muestra el número; la descripción sale en el PDF). */}
          {nodes.some(n => n.kind === 'ref') && (
            <div className="epl-section">
              <div className="epl-section-title">Referencias</div>
              <div className="epl-refs-list">
                {nodes.filter(n => n.kind === 'ref').sort((a, b) => a.num - b.num).map(r => (
                  <div key={r.id} className={`epl-ref-row ${selectedId === r.id ? 'sel' : ''}`}>
                    <span className="epl-ref-num">{r.num}</span>
                    <input
                      className="epl-ref-input"
                      value={r.descripcion || ''}
                      placeholder="Descripción…"
                      onChange={(e) => updateNode(r.id, { descripcion: e.target.value })}
                      onFocus={() => setSelectedId(r.id)}
                    />
                    <button className="epl-ref-del" title="Eliminar referencia" onClick={() => pushHistory(nodes.filter(n => n.id !== r.id))}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {paleta.length > 0 && (
            <div className="epl-section">
              <div className="epl-section-title">Paleta: {tipoActual?.nombre}</div>
              <div className="epl-palette-grid">
                {paleta.map(item => (
                  <button
                    key={item.id}
                    className="epl-palette-item"
                    onClick={() => addFromPalette(item)}
                    title={item.label}
                  >
                    {item.src ? (
                      <img src={item.src} alt={item.label} />
                    ) : (
                      <div
                        className="epl-palette-shape"
                        style={{
                          background: item.fill && item.fill !== 'transparent' ? item.fill : 'transparent',
                          border: `${item.strokeWidth || 2}px solid ${item.stroke}`,
                        }}
                      />
                    )}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

// Subcomponente para iconos (necesita useImage por separado)
const IconNode = ({ node, common }) => {
  const [img] = useImage(node.src, 'anonymous');
  if (!img) return null;
  return (
    <KImage
      {...common}
      image={img}
      x={node.x} y={node.y}
      width={node.width} height={node.height}
      rotation={node.rotation || 0}
    />
  );
};

// Subcomponente para notas (fondo amarillo + texto)
const NoteNode = ({ node, common, onDblClick }) => {
  return (
    <React.Fragment>
      <Rect
        {...common}
        x={node.x} y={node.y}
        width={node.width} height={node.height}
        fill={node.bg || 'rgba(254,240,138,0.9)'}
        stroke="#ca8a04" strokeWidth={1}
        cornerRadius={4}
        rotation={node.rotation || 0}
        onDblClick={onDblClick} onDblTap={onDblClick}
      />
      <Text
        listening={false}
        x={node.x + 6} y={node.y + 6}
        text={node.text || ''}
        fontSize={node.fontSize || 14}
        fill={node.fill || '#111827'}
        width={(node.width || 160) - 12}
        rotation={node.rotation || 0}
      />
    </React.Fragment>
  );
};

// Flecha: Group con linea delgada + puntas pequeñas. Soporta 3 variantes:
//   'simple' → linea recta con una punta a la derecha
//   'double' → linea recta con puntas a izquierda y derecha
//   'curve'  → linea curvada (tension) con una punta al final
// Convierte un color solido (#rrggbb) en semitransparente para que el plano
// de fondo se siga viendo debajo del trazo. Si ya trae alpha, lo respeta.
const conAlpha = (color, alpha = 0.75) => {
  if (!color || typeof color !== 'string') return color;
  const c = color.trim();
  if (c.startsWith('rgba') || c === 'transparent') return c;
  const hex = c.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return c;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ArrowNode = ({ node, common }) => {
  const sw = node.strokeWidth || 3;
  const stroke = node.stroke || '#1c2837';
  const variant = node.variant || (node.doubleHead ? 'double' : 'simple'); // compat con flechas viejas
  // Tamaño de las puntas escala con el bbox (proporcional al alto)
  const headLen = Math.max(6, Math.min(14, node.height * 0.7));    // largo de la punta
  const headW   = Math.max(4, Math.min(10, node.height * 0.5));    // ancho de cada lado de la punta
  const midY = node.height / 2;
  const leftX = variant === 'double' ? headLen : 2;
  const rightX = node.width - headLen;

  // HIT AREA: rect invisible (no transparente puro porque Konva no detecta
  // fill 'transparent'). rgba(0,0,0,0.001) es invisible al ojo pero clickeable.
  // Esto garantiza que toda el area del bbox del Group reciba click/drag, no
  // solo donde caen los strokes de los hijos.
  const hitRect = (
    <Rect x={0} y={0} width={node.width} height={node.height} fill="rgba(0,0,0,0.001)" />
  );

  if (variant === 'curve') {
    // Curva: linea con tension > 0. Tres puntos: inicio, control (mas abajo), fin.
    // La punta es un triangulo con la inclinacion del tramo final.
    const x0 = 4, y0 = node.height - 4;
    const xC = node.width / 2, yC = -node.height * 0.15;
    const x1 = node.width - 6, y1 = node.height * 0.5;
    const tx = x1 - xC, ty = y1 - yC;
    const tlen = Math.sqrt(tx * tx + ty * ty) || 1;
    const ux = tx / tlen, uy = ty / tlen;
    const px = -uy, py = ux;
    const baseX = x1 - ux * headLen;
    const baseY = y1 - uy * headLen;
    return (
      <Group {...common} x={node.x} y={node.y} width={node.width} height={node.height} rotation={node.rotation || 0}>
        {hitRect}
        <Line
          listening={false}
          points={[x0, y0, xC, yC, x1, y1]}
          tension={0.6}
          stroke={stroke} strokeWidth={sw}
          lineCap="round" lineJoin="round"
        />
        <Line
          listening={false}
          points={[
            baseX + px * headW, baseY + py * headW,
            x1, y1,
            baseX - px * headW, baseY - py * headW,
          ]}
          stroke={stroke} strokeWidth={sw}
          fill={stroke} closed
          lineCap="round" lineJoin="round"
        />
      </Group>
    );
  }

  // Simple / doble
  return (
    <Group {...common} x={node.x} y={node.y} width={node.width} height={node.height} rotation={node.rotation || 0}>
      {hitRect}
      {/* Cuerpo: linea delgada centrada verticalmente */}
      <Line
        listening={false}
        points={[leftX, midY, rightX, midY]}
        stroke={stroke} strokeWidth={sw}
        lineCap="round"
      />
      {/* Punta derecha (triangulo) */}
      <Line
        listening={false}
        points={[
          rightX, midY - headW,
          node.width - 2, midY,
          rightX, midY + headW,
        ]}
        stroke={stroke} strokeWidth={sw}
        fill={stroke} closed
        lineCap="round" lineJoin="round"
      />
      {/* Punta izquierda (solo doble) */}
      {variant === 'double' && (
        <Line
          listening={false}
          points={[
            headLen, midY - headW,
            2, midY,
            headLen, midY + headW,
          ]}
          stroke={stroke} strokeWidth={sw}
          fill={stroke} closed
          lineCap="round" lineJoin="round"
        />
      )}
    </Group>
  );
};

// Referencia numerada: circulo gris con un numero
const RefNumNode = ({ node, common }) => {
  const r = Math.min(node.width, node.height) / 2;
  return (
    <Group {...common} x={node.x} y={node.y} width={node.width} height={node.height} rotation={node.rotation || 0}>
      <Circle x={r} y={r} radius={r} fill="#9ca3af" stroke="#475569" strokeWidth={1.5} />
      <Text
        x={0} y={0} width={node.width} height={node.height}
        text={String(node.num || 0)}
        fontSize={Math.max(10, node.height * 0.5)}
        fontStyle="bold"
        fill="#1c2837"
        align="center" verticalAlign="middle"
        listening={false}
      />
    </Group>
  );
};

// Nota numerada: tarjeta blanca con "(N)" + texto descriptivo
const RefNoteNode = ({ node, common, onDblClick }) => {
  return (
    <Group {...common} x={node.x} y={node.y} width={node.width} height={node.height} rotation={node.rotation || 0}>
      <Rect
        x={0} y={0} width={node.width} height={node.height}
        fill={node.bg || 'rgba(255,255,255,0.95)'}
        stroke="#cbd5e1" strokeWidth={1}
        cornerRadius={6}
        onDblClick={onDblClick} onDblTap={onDblClick}
      />
      <Circle x={18} y={18} radius={11} fill="#9ca3af" stroke="#475569" strokeWidth={1} listening={false} />
      <Text
        x={8} y={8} width={20} height={20}
        text={String(node.num || 0)}
        fontSize={11} fontStyle="bold" fill="#1c2837"
        align="center" verticalAlign="middle"
        listening={false}
      />
      <Text
        x={40} y={10}
        text={node.text || ''}
        fontSize={node.fontSize || 13}
        fill={node.fill || '#111827'}
        width={node.width - 50}
        height={node.height - 16}
        listening={false}
      />
    </Group>
  );
};

// Indicador compacto del estado del autoguardado en el toolbar
const AutoSaveIndicator = ({ status, lastSavedAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  let txt = '';
  let cls = 'epl-auto-idle';
  if (status === 'saving') { txt = 'Guardando...'; cls = 'epl-auto-saving'; }
  else if (status === 'pending') { txt = 'Cambios sin guardar...'; cls = 'epl-auto-pending'; }
  else if (status === 'error') { txt = 'Error al guardar'; cls = 'epl-auto-error'; }
  else if (status === 'saved' && lastSavedAt) {
    const s = Math.max(0, Math.floor((now - lastSavedAt.getTime()) / 1000));
    if (s < 5) txt = '✓ Guardado';
    else if (s < 60) txt = `✓ Guardado hace ${s}s`;
    else txt = `✓ Guardado hace ${Math.floor(s / 60)}m`;
    cls = 'epl-auto-saved';
  }
  if (!txt) return null;
  return <span className={`epl-auto-indicator ${cls}`}>{txt}</span>;
};

export default EditorPlanos;
